/*!
 * bz-game.js — The Focus Challenge item-renderer engine
 * Session 32D · projects/briefs/focus-game-2026-08-04/tactics.md
 *
 * COMPLIANCE GUARDRAILS BAKED IN (do not remove):
 *  - The science is about the PHENOMENON, never about the user. No deficit framing,
 *    no "brain age", no age-normed percentile, no red/warning result states.
 *  - Score NEVER drives the product recommendation. Routing comes from separately-asked
 *    preference questions only. Banned connective copy is asserted at runtime (assertNoConnective).
 *  - Score is session-only. Never persisted as a customer trait; never sent to CRM.
 *  - Percentile is vs OTHER PLAYERS of this game, per market. Never vs an age norm.
 *
 * No dependencies. Designed to lift into assets/bz-game.js and mount inside the
 * existing bz-quiz spine (which owns email gate, outcome routing, result render, relay).
 */
(function (root) {
  'use strict';

  var REDUCED_MOTION = root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */
  function el(tag, cls, txt) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (txt != null) n.textContent = txt;
    return n;
  }
  function now() { return (root.performance && root.performance.now) ? root.performance.now() : Date.now(); }
  function clear(n) { while (n.firstChild) n.removeChild(n.firstChild); }
  function shuffle(a, rnd) {
    var arr = a.slice();
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(rnd() * (i + 1));
      var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
  }
  // Seeded PRNG (mulberry32) so a "daily challenge" can be identical for everyone in a market.
  function makeRng(seed) {
    var s = seed >>> 0;
    return function () {
      s |= 0; s = (s + 0x6D2B79F5) | 0;
      var t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function dailySeed(marketSalt) {
    var d = new Date();
    var key = '' + d.getUTCFullYear() + (d.getUTCMonth() + 1) + d.getUTCDate() + (marketSalt || '');
    var h = 2166136261;
    for (var i = 0; i < key.length; i++) { h ^= key.charCodeAt(i); h = Math.imul(h, 16777619); }
    return h >>> 0;
  }

  /* ------------------------------------------------- no-repeat item memory */
  var SEEN_KEY = 'bz_game_seen_v1';
  function loadSeen() {
    try { return JSON.parse(root.localStorage.getItem(SEEN_KEY) || '{}'); } catch (e) { return {}; }
  }
  function saveSeen(s) {
    try { root.localStorage.setItem(SEEN_KEY, JSON.stringify(s)); } catch (e) { /* private mode */ }
  }
  /**
   * Draw one item from a typed sub-pool, no-repeat-until-exhausted.
   * Composition is FIXED (one of each mechanic); only the content is random.
   */
  function drawFromPool(slotKey, pool, rnd) {
    var seen = loadSeen();
    var used = seen[slotKey] || [];
    var fresh = pool.filter(function (it) { return used.indexOf(it.id) === -1; });
    if (!fresh.length) { used = []; fresh = pool.slice(); }
    var pick = fresh[Math.floor(rnd() * fresh.length)];
    used.push(pick.id);
    seen[slotKey] = used;
    saveSeen(seen);
    return pick;
  }


  /**
   * PER-TRIAL VERDICT (Calum 2026-08-06 — whole-set policy).
   *
   * Every scored trial now shows a brief right/wrong mark. Testers could not tell whether they
   * had understood a rule, which made a misunderstanding indistinguishable from being bad at it.
   * Running blind is a LAB convention — it stops feedback changing a participant's strategy
   * mid-experiment. We are not running an experiment, and the cost here is real: no feedback
   * means no chance to learn the rule, and no moment-to-moment reward.
   *
   * Deliberately quiet: a small mark, ~380ms, never red-alarm styling, never a running
   * "3 wrong" tally. It tells you what happened without keeping score against you.
   */
  function flashVerdict(stage, ok) {
    var m = el('div', 'bzg-verdict ' + (ok ? 'is-ok' : 'is-no'), ok ? '\u2713' : '\u2715');
    m.setAttribute('aria-hidden', 'true');
    stage.appendChild(m);
    setTimeout(function () { if (m.parentNode) m.parentNode.removeChild(m); }, 380);
  }

  /* --------------------------------------------------------------- copy/i18n */
  var STRINGS = {
    en: {
      ready: 'Ready?', go: 'GO', tapWhenGreen: 'Tap the moment it turns green',
      tooSoon: 'Too soon — wait for green', tapChanged: 'Tap the square that keeps changing',
      whichColour: 'Tap the COLOUR of the ink, not the word',
      whichShape: 'Tap the SHAPE you see, not the word',
      repeatSeq: 'Repeat the sequence', quickMaths: 'Quick maths',
      round: 'Round', of: 'of', next: 'Next', skip: 'Skip this round',
      untimed: 'Untimed mode', playAgain: 'Play again',
      stopInZone: 'Stop the marker inside the yellow zone', stop: 'STOP',
      oddOneOut: 'Tap the odd one out',
      byColour: 'BY COLOUR', byShape: 'BY SHAPE',
      countBlue: 'Watch for the BLUE ones', howManyBlue: 'How many blue did you see?',
      howMany: 'How many dots?',
      matchColour: 'Answer by COLOUR, ignore which side it is on',
      tapMiddle: 'Tap the exact middle of the line',
      rememberWords: 'Remember these words', wasItShown: 'Was this word shown?',
      seenIt: 'I saw it', newWord: 'New'
    }
    // de/fr/us added at integration; engine is market-agnostic.
  };

  /* ------------------------------------------------------ science explainers
   * Each explainer describes the PHENOMENON. Never the player's deficiency. */
  var SCIENCE = {
    reaction: {
      name: 'Simple reaction time',
      explain: function (r) {
        return 'You reacted in ' + Math.round(r.rtMs) + 'ms. Signal travels eye → brain → thumb, and '
             + 'almost none of that is thinking — most of it is wiring. Nobody beats about 100ms.';
      }
    },
    change: {
      name: 'Change blindness',
      explain: function () {
        // Phenomenon, never the person. "You didn't see it" is deficit framing and breaks
        // the design principle in tactics.md §2. (QC — GLM 5.2, 2026-08-04.)
        return 'Most people miss it for several flickers. That is change blindness: when a flicker '
             + 'interrupts the scene, the brain does not store the whole picture — it re-samples only '
             + 'what it expects to matter.';
      }
    },
    stroop: {
      name: 'The Stroop effect',
      explain: function (r) {
        var d = r.interferenceMs;
        return (d != null && d > 0)
          ? 'You were ' + Math.round(d) + 'ms slower when the word fought the ink. That gap is Stroop '
            + 'interference — reading is so automatic your brain has to actively suppress it. Replicated since 1935.'
          : 'That gap between matching and conflicting trials is Stroop interference — reading is automatic, '
            + 'so the brain must actively suppress it. Replicated since 1935.';
      }
    },
    sequence: {
      name: 'Working-memory span',
      explain: function (r) {
        return 'You held ' + r.span + ' in order. Most people top out around four chunks — that is not a '
             + 'flaw, it is the documented capacity of working memory.';
      }
    },
    switch: {
      name: 'Task-switching cost',
      explain: function (r) {
        var d = r.switchCostMs;
        return (d != null && d > 0)
          ? 'You lost about ' + Math.round(d) + 'ms every time the rule changed. That gap is the switch '
            + 'cost, and it is why nobody really multitasks \u2014 you are not doing two things at once, you '
            + 'are paying a toll every time you swap between them.'
          : 'The gap between rule-repeat and rule-change trials is the switch cost. It is why nobody '
            + 'really multitasks \u2014 you are not doing two things at once, you are paying a toll to swap.';
      }
    },
    blink: {
      name: 'The attentional blink',
      explain: function (r) {
        var missed = (r.shortAcc != null && r.longAcc != null && r.shortAcc < r.longAcc);
        return missed
          ? 'You caught the second target when it came late, and missed it when it came close behind '
            + 'the first. That is the attentional blink: once attention locks onto something, it is '
            + 'briefly unavailable \u2014 for roughly a quarter to half a second.'
          : 'When a second target lands within about half a second of the first, most people simply do '
            + 'not see it. Attention is not a steady searchlight; it has a refractory period.';
      }
    },
    subitise: {
      name: 'Subitising',
      explain: function (r) {
        var d = (r.largeMs != null && r.smallMs != null) ? Math.round(r.largeMs - r.smallMs) : null;
        return (d != null && d > 0)
          ? 'The bigger sets took you ' + d + 'ms longer. Up to about four you do not count \u2014 you just '
            + 'know. Past four the brain switches to counting, and the clock shows exactly where the '
            + 'switch happens.'
          : 'Up to about four you do not count, you just know. Past four the brain switches to counting. '
            + 'The jump between the two is one of the crispest findings in perception.';
      }
    },
    simon: {
      name: 'The Simon effect',
      explain: function (r) {
        var d = r.simonMs;
        return (d != null && d > 0)
          ? 'You were ' + Math.round(d) + 'ms slower when the colour and the side disagreed. Nothing asked '
            + 'you to care where it appeared \u2014 but position gets processed anyway and has to be overruled. '
            + 'No reading involved, so this one works identically in every language.'
          : 'Position gets processed whether it is relevant or not, and has to be overruled when it '
            + 'disagrees with what you are answering on. That conflict is the Simon effect.';
      }
    },
    bisect: {
      name: 'Pseudoneglect',
      explain: function (r) {
        var b = r.biasPct;
        return (b != null && b < -0.5)
          ? 'You marked about ' + Math.abs(Math.round(b * 10) / 10) + '% LEFT of true centre. Most people do. '
            + 'It is called pseudoneglect \u2014 the right hemisphere pulls attention slightly leftward, and it '
            + 'shows up in healthy people every time.'
          : 'Most people mark slightly LEFT of true centre \u2014 pseudoneglect, a leftward pull in normal '
            + 'attention. Your marks sat close to the middle.';
      }
    },
    oddone: {
      name: 'Pop-out vs serial search',
      explain: function (r) {
        var d = (r.conjMs != null && r.popMs != null) ? Math.round(r.conjMs - r.popMs) : null;
        return (d != null && d > 0)
          ? 'The second grid took you ' + d + 'ms longer. In the first, the odd one differed by ONE '
            + 'feature, so it pops out — you could have doubled the grid and it would be just as fast. '
            + 'In the second it differed only by a COMBINATION, and combinations have to be checked '
            + 'one at a time.'
          : 'When the odd one differs by ONE feature it pops out and grid size stops mattering. When it '
            + 'differs only by a COMBINATION of features, you have to check items one at a time.';
      }
    },
    timing: {
      name: 'Anticipation timing',
      explain: function (r) {
        return 'You were off by about ' + Math.round(r.offBy) + '% of the track. This is not '
             + 'reaction — you cannot react to a moving target and still be on time. You have to '
             + 'predict where it WILL be. Different faculty, different part of the brain.';
      }
    },
    words: {
      name: 'False memory',
      explain: function (r) {
        return r.lureFooled
          ? 'You were sure you saw “' + r.lureWord + '”. It was never shown. Your brain stores the '
            + 'GIST of a list, not the words — so a word that fits the theme feels remembered. '
            + 'Most people fall for it; it is one of the most reliable effects in memory research.'
          : 'The list had a trap in it — “' + r.lureWord + '” fits the theme but was never shown. '
            + 'Most people recognise it anyway, because the brain stores the gist rather than the words. '
            + 'You did not.';
      }
    },
    maths: {
      name: 'Processing speed under load',
      explain: function () {
        return 'Speed and accuracy trade against each other under time pressure. Push one and the other '
             + 'gives — the trade-off is the finding, not your total.';
      }
    }
  };

  /**
   * The measured number for a round, as a bare stat sentence. Deliberately factual and
   * comparative-to-nothing — no norm, no percentile, no age reference (tactics.md §3).
   * Returns '' when there is nothing honest to report.
   */
  function measuredStat(r, t) {
    // `t` is the market's `stat` template bag. These sentences are prepended to the science card,
    // so they MUST be localised — they were hardcoded English, which meant DE and FR players read
    // an English sentence glued to the front of their own copy (found 2026-08-07).
    t = t || {};
    function f(key, tokens) {
      var s = t[key];
      if (!s) return '';
      for (var k in tokens) {
        if (Object.prototype.hasOwnProperty.call(tokens, k)) {
          s = s.replace('{' + k + '}', String(tokens[k]));
        }
      }
      return s;
    }
    function pct(a) { return Math.round((a || 0) * 100); }

    if (r.type === 'reaction' && r.rtMs != null) return f('reaction', { ms: Math.round(r.rtMs) });
    if (r.type === 'stroop' && r.interferenceMs != null && r.interferenceMs > 0) {
      return f('stroop', { ms: Math.round(r.interferenceMs) });
    }
    if (r.type === 'sequence' && r.span != null) return f('sequence', { n: r.span });
    if (r.type === 'simon' && r.simonMs != null && r.simonMs > 0) {
      return f('simon', { ms: Math.round(r.simonMs) });
    }
    if (r.type === 'switch' && r.switchCostMs != null && r.switchCostMs > 0) {
      return f('switch', { ms: Math.round(r.switchCostMs) });
    }
    if (r.type === 'timing' && r.offBy != null) return f('timing', { pct: Math.round(r.offBy) });
    if (r.type === 'bisect' && r.biasPct != null) {
      return f('bisect', { pct: Math.round(Math.abs(r.biasPct) * 10) / 10 });
    }
    // the remaining rounds expose accuracy only — still the player's own number
    if (r.type === 'subitise' && r.accuracy != null) return f('subitise', { pct: pct(r.accuracy) });
    if (r.type === 'oddone' && r.accuracy != null) return f('oddone', { pct: pct(r.accuracy) });
    if (r.type === 'blink' && r.accuracy != null) return f('blink', { pct: pct(r.accuracy) });
    if (r.type === 'words' && r.accuracy != null) return f('words', { pct: pct(r.accuracy) });
    return '';
  }

  /* ------------------------------------------------------------ item pools */
  var POOLS = {
    reaction: [
      { id: 'rx1', minDelay: 1200, maxDelay: 3200 },
      { id: 'rx2', minDelay: 900,  maxDelay: 2600 },
      { id: 'rx3', minDelay: 1500, maxDelay: 3800 },
      { id: 'rx4', minDelay: 1000, maxDelay: 2900 },
      { id: 'rx5', minDelay: 1300, maxDelay: 3400 }
    ],
    change: [
      { id: 'cb1', grid: 4, changed: 6 },  { id: 'cb2', grid: 4, changed: 11 },
      { id: 'cb3', grid: 4, changed: 2 },  { id: 'cb4', grid: 5, changed: 17 },
      { id: 'cb5', grid: 5, changed: 8 },  { id: 'cb6', grid: 4, changed: 14 },
      { id: 'cb7', grid: 5, changed: 21 }, { id: 'cb8', grid: 4, changed: 9 }
    ],
    stroop: [
      { id: 'st1', trials: 6 }, { id: 'st2', trials: 6 }, { id: 'st3', trials: 8 },
      { id: 'st4', trials: 6 }, { id: 'st5', trials: 8 }
    ],
    sequence: [
      { id: 'sq1', start: 3 }, { id: 'sq2', start: 3 }, { id: 'sq3', start: 4 },
      { id: 'sq4', start: 3 }, { id: 'sq5', start: 4 }
    ],
    maths: [
      { id: 'mt1', ops: ['+', '-'], max: 20, trials: 5 },
      { id: 'mt2', ops: ['+', '-'], max: 30, trials: 5 },
      { id: 'mt3', ops: ['+', '-', 'x'], max: 12, trials: 5 },
      { id: 'mt4', ops: ['+', '-'], max: 25, trials: 6 },
      { id: 'mt5', ops: ['x'], max: 9, trials: 5 }
    ]
  };

  /* ================================================== ROUND 1 — reaction tap
   * TWO trials, reported as the mean (Calum playtest 2026-08-05: "the 1st test was good,
   * maybe there can be 2 rounds of this as it's simple and fun"). Two trials is also better
   * measurement — a single reaction time is a famously noisy statistic. */
  function playReaction(ctx, item, done) {
    var s = ctx.s, stage = ctx.stage;
    var TRIALS = item.trials || 2;
    var trial = 0, times = [];

    function runOne() {
      trial++;
      clear(stage);
      if (TRIALS > 1) {
        stage.appendChild(el('p', 'bzg-prompt', s.tapWhenGreen + '  (' + trial + '/' + TRIALS + ')'));
      }
      var pad = el('button', 'bzg-pad bzg-pad--wait');
      pad.type = 'button';
      pad.setAttribute('aria-live', 'polite');
      pad.textContent = TRIALS > 1 ? '' : s.tapWhenGreen;
      stage.appendChild(pad);

      var armed = false, t0 = 0, timer = null, finished = false;
      var delay = item.minDelay + Math.random() * (item.maxDelay - item.minDelay);

      function arm() {
        armed = true; t0 = now();
        pad.className = 'bzg-pad bzg-pad--go';
        pad.textContent = s.go;
      }
      timer = setTimeout(arm, delay);

      // TIMING: pointerdown, NOT click. `click` fires on RELEASE, so it would measure
      // press+release rather than reaction, and inherit the mobile tap delay.
      pad.style.touchAction = 'manipulation';
      pad.addEventListener('pointerdown', function (ev) {
        if (ev.cancelable) ev.preventDefault();
        if (finished) return;
        if (!armed) {                     // premature — not a failure, just re-arm
          clearTimeout(timer);
          pad.className = 'bzg-pad bzg-pad--wait';
          pad.textContent = s.tooSoon;
          timer = setTimeout(arm, 900 + Math.random() * 2200);
          return;
        }
        finished = true;
        times.push(now() - t0);
        flashVerdict(stage, true);
        if (trial < TRIALS) { setTimeout(runOne, 620); }
        else {
          var mean = times.reduce(function (a, b) { return a + b; }, 0) / times.length;
          // hold so the final mark is actually seen before the science card replaces it
          setTimeout(function () {
            done({ type: 'reaction', correct: true, rtMs: mean,
                   raw: { trials: times.map(Math.round) } });
          }, 460);
        }
      });
    }
    runOne();
  }

  /* ============================ ROUND 6 — moving-target timing (NEW)
   * Calum 2026-08-05: "something is moving left or right and the user has to press
   * something to match the timing". This is anticipation timing / temporal prediction —
   * a real, distinct faculty from simple reaction time: you must predict where a moving
   * object WILL be, not respond to where it is. */
  function playTiming(ctx, item, done) {
    var s = ctx.s, stage = ctx.stage;
    clear(stage);
    stage.appendChild(el('p', 'bzg-prompt', s.stopInZone));

    var track = el('div', 'bzg-track');
    var zone = el('div', 'bzg-zone');
    var marker = el('div', 'bzg-marker');
    track.appendChild(zone); track.appendChild(marker);
    stage.appendChild(track);

    var btn = el('button', 'bzg-btn bzg-stopbtn', s.stop);
    btn.type = 'button';
    btn.style.touchAction = 'manipulation';
    stage.appendChild(btn);

    var TRIALS = item.trials || 3;
    var trial = 0, errors = [];
    var raf = null, startT = 0, period = item.period || 1500, dir = 1;
    var zoneCentre = 50, zoneHalf = item.zoneHalf || 7;   // % of track
    zone.style.left = (zoneCentre - zoneHalf) + '%';
    zone.style.width = (zoneHalf * 2) + '%';

    function frame() {
      var t = (now() - startT) % (period * 2);
      var p = t < period ? (t / period) : (2 - t / period);   // 0→1→0 triangle
      marker.style.left = (p * 100) + '%';
      raf = requestAnimationFrame(frame);
    }
    function startTrial() {
      trial++;
      period = (item.period || 1500) - (trial - 1) * 180;      // speeds up each trial
      startT = now();
      raf = requestAnimationFrame(frame);
    }
    function stopTrial() {
      cancelAnimationFrame(raf);
      // null it: a cancelled id is still truthy, so the `if (raf)` guard on the STOP button
      // let a second press re-enter here and push a spurious error against a stale clock
      // — and double-schedule done() on the final trial (T-e193fa)
      raf = null;
      var t = (now() - startT) % (period * 2);
      var p = t < period ? (t / period) : (2 - t / period);
      var errPct = Math.abs(p * 100 - zoneCentre);
      errors.push(errPct);
      marker.classList.toggle('is-hit', errPct <= zoneHalf);
      setTimeout(function () {
        marker.classList.remove('is-hit');
        if (trial < TRIALS) startTrial();
        else {
          var mean = errors.reduce(function (a, b) { return a + b; }, 0) / errors.length;
          var hits = errors.filter(function (e) { return e <= zoneHalf; }).length;
          done({ type: 'timing', correct: hits > 0, accuracy: hits / TRIALS,
                 offBy: mean, rtMs: null, raw: { errors: errors.map(function (e) { return Math.round(e); }) } });
        }
      }, 420);
    }
    btn.addEventListener('pointerdown', function (ev) {
      if (ev.cancelable) ev.preventDefault();
      if (raf) stopTrial();
    });
    startTrial();
    ctx.onAbort = function () { cancelAnimationFrame(raf); };
  }

  /* ============================================ ROUND 2 — change blindness */
  function playChange(ctx, item, done) {
    var s = ctx.s, stage = ctx.stage;
    clear(stage);
    stage.appendChild(el('p', 'bzg-prompt', s.tapChanged));

    var n = item.grid * item.grid;
    var palette = ['#269BF4', '#FFC42E', '#6d6e71', '#0A5FB8', '#eaeced'];
    var base = [], i;
    for (i = 0; i < n; i++) base.push(palette[Math.floor(Math.random() * palette.length)]);
    var alt = base.slice();
    var ci = item.changed % n;
    // guarantee a visible change
    var swap = palette[(palette.indexOf(base[ci]) + 2) % palette.length];
    alt[ci] = swap;

    var grid = el('div', 'bzg-grid');
    grid.style.gridTemplateColumns = 'repeat(' + item.grid + ', 1fr)';
    var cells = [];
    for (i = 0; i < n; i++) {
      var c = el('button', 'bzg-cell');
      c.type = 'button';
      c.setAttribute('aria-label', 'cell ' + (i + 1));
      c.style.background = base[i];
      (function (idx) {
        onTap(c, function () {
          if (fin) return;
          fin = true;
          clearTimeout(flick);
          var rt = now() - t0;
          done({ type: 'change', correct: idx === ci, rtMs: rt, raw: { picked: idx, target: ci } });
        });
      })(i);
      cells.push(c); grid.appendChild(c);
    }
    stage.appendChild(grid);

    // TRUE flicker paradigm: A → BLANK → B → BLANK. The blank is the whole mechanism —
    // it wipes the motion transient that would otherwise make the change pop out.
    // (Calum playtest 2026-08-05: "2nd test was way too easy" — this was why: the original
    // alternated A/B with no blank, so low-level motion detection solved it instantly.)
    var phase = 0, fin = false, t0 = now();
    var BLANK = '#141c28';
    var onMs  = REDUCED_MOTION ? 700 : 420;
    var offMs = REDUCED_MOTION ? 260 : 150;

    function paint(src) { for (var k = 0; k < n; k++) cells[k].style.background = src[k]; }
    function blank() { for (var k = 0; k < n; k++) cells[k].style.background = BLANK; }

    var flick = null;
    (function cycle() {
      if (fin) return;
      var src = (phase % 2 === 0) ? base : alt;
      paint(src);
      flick = setTimeout(function () {
        if (fin) return;
        blank();
        flick = setTimeout(function () { phase++; cycle(); }, offMs);
      }, onMs);
    })();

    ctx.onAbort = function () { fin = true; clearTimeout(flick); };
  }

  /* ==================================================== ROUND 3 — Stroop */
  function playStroop(ctx, item, done) {
    var s = ctx.s, stage = ctx.stage;
    var colourSafe = ctx.opts.colourBlindSafe;
    clear(stage);
    stage.appendChild(el('p', 'bzg-prompt', colourSafe ? s.whichShape : s.whichColour));

    var COLOURS = [
      { key: 'blue',   hex: '#0A5FB8', label: 'BLUE' },
      { key: 'yellow', hex: '#E0A800', label: 'YELLOW' },
      { key: 'grey',   hex: '#6d6e71', label: 'GREY' },
      { key: 'green',  hex: '#1B7F4C', label: 'GREEN' }
    ];
    var SHAPES = [
      { key: 'circle',   label: 'CIRCLE',   glyph: '●' },
      { key: 'square',   label: 'SQUARE',   glyph: '■' },
      { key: 'triangle', label: 'TRIANGLE', glyph: '▲' },
      { key: 'star',     label: 'STAR',     glyph: '★' }
    ];
    // Stroop words are the ONLY localised game content — take the market lexicon when given.
    var lex = ctx.opts.lexicon;
    var SET = colourSafe
      ? (lex && lex.shapes  ? lex.shapes  : SHAPES)
      : (lex && lex.colours ? lex.colours : COLOURS);

    var word = el('div', 'bzg-stroop-word');
    stage.appendChild(word);
    var opts = el('div', 'bzg-opts');
    stage.appendChild(opts);

    var trial = 0, congruentRTs = [], incongruentRTs = [], hits = 0, t0 = 0, cur = null, curCong = false;

    function nextTrial() {
      if (trial >= item.trials) return finish();
      trial++;
      // alternate congruent/incongruent so interference is measurable
      curCong = (trial % 2 === 1);
      var shown = SET[Math.floor(Math.random() * SET.length)];
      var truth = curCong ? shown : SET[(SET.indexOf(shown) + 1 + Math.floor(Math.random() * (SET.length - 1))) % SET.length];
      cur = truth;

      clear(word);
      if (colourSafe) {
        word.textContent = truth.glyph;
        word.style.color = '#222';
        var cap = el('div', 'bzg-stroop-cap', shown.label);
        word.appendChild(cap);
      } else {
        word.textContent = shown.label;
        word.style.color = truth.hex;
      }

      clear(opts);
      // one answer per trial — two options tapped in the 380ms window each scored, and on the
      // last trial each independently reached finish(): the literal "125% right" path (T-e193fa)
      var answered = false;
      shuffle(SET, Math.random).forEach(function (o) {
        var b = el('button', 'bzg-opt', colourSafe ? o.label : o.label);
        b.type = 'button';
        if (!colourSafe) b.style.borderColor = o.hex;
        onTap(b, function () {
          if (answered) return;
          answered = true;
          var rt = now() - t0;
          var ok = (o.key === cur.key);
          if (ok) hits++;
          (curCong ? congruentRTs : incongruentRTs).push(rt);
          flashVerdict(stage, ok);
          setTimeout(nextTrial, 380);
        });
        opts.appendChild(b);
      });
      t0 = now();
    }

    function mean(a) { return a.length ? a.reduce(function (x, y) { return x + y; }, 0) / a.length : null; }

    function finish() {
      var mc = mean(congruentRTs), mi = mean(incongruentRTs);
      var interference = (mc != null && mi != null) ? (mi - mc) : null;
      done({
        type: 'stroop',
        correct: hits >= Math.ceil(item.trials * 0.6),
        accuracy: hits / item.trials,
        rtMs: mean(congruentRTs.concat(incongruentRTs)),
        interferenceMs: interference,
        raw: { hits: hits, trials: item.trials, congruent: mc, incongruent: mi }
      });
    }
    nextTrial();
  }

  /* ================================================== ROUND 4 — sequence span */
  function playSequence(ctx, item, done) {
    var s = ctx.s, stage = ctx.stage;
    clear(stage);
    stage.appendChild(el('p', 'bzg-prompt', s.repeatSeq));

    var grid = el('div', 'bzg-grid bzg-grid--4');
    grid.style.gridTemplateColumns = 'repeat(2, 1fr)';
    var tiles = [];
    for (var i = 0; i < 4; i++) {
      var t = el('button', 'bzg-tile');
      t.type = 'button';
      t.setAttribute('aria-label', 'tile ' + (i + 1));
      (function (idx) {
        // pointerdown, not click: click fires on RELEASE, which is exactly the lag
        // Calum reported 2026-08-06 ("a little bit of a lag between pressing and
        // seeing it register"). Same defect class as the reaction round.
        t.style.touchAction = 'manipulation';
        t.addEventListener('pointerdown', function (ev) {
          if (ev.cancelable) ev.preventDefault();
          onTap(idx);
        });
      })(i);
      tiles.push(t); grid.appendChild(t);
    }
    stage.appendChild(grid);

    var len = item.start, seq = [], inputIdx = 0, accepting = false, best = 0, t0 = now();
    // Calum 2026-08-06, scoring fix B. This round is ADAPTIVE: the better you do, the longer
    // you play. Reporting round DURATION as rtMs therefore inverted the speed term — failing
    // fast scored better than succeeding. rtMs is now the mean per-tap latency during recall,
    // which is a genuine speed measure and comparable with every other round.
    var tapLats = [], lastTapT = null;
    function meanOf(a) { return a.length ? a.reduce(function (x, y) { return x + y; }, 0) / a.length : null; }

    function flash(idx, cb) {
      tiles[idx].classList.add('is-lit');
      setTimeout(function () { tiles[idx].classList.remove('is-lit'); cb && cb(); },
                 REDUCED_MOTION ? 520 : 380);
    }
    function playSeq() {
      accepting = false; inputIdx = 0;
      var k = 0;
      (function step() {
        // the recall clock starts when playback ENDS, so playback time is never charged to the player
        if (k >= seq.length) { accepting = true; lastTapT = now(); return; }
        flash(seq[k], function () { k++; setTimeout(step, REDUCED_MOTION ? 260 : 160); });
      })();
    }
    function nextRound() {
      seq = [];
      for (var i = 0; i < len; i++) seq.push(Math.floor(Math.random() * 4));
      setTimeout(playSeq, 420);
    }
    function onTap(idx) {
      if (!accepting) return;
      var tapT = now();
      if (lastTapT != null) tapLats.push(tapT - lastTapT);
      lastTapT = tapT;
      // Calum 2026-08-05: "register key presses by the user when the user is trying to
      // recreate the sequence" — the user got NO confirmation their tap landed. Echo it.
      flash(idx);
      if (seq[inputIdx] === idx) {
        inputIdx++;
        if (inputIdx === seq.length) {
          // Level cleared. Stop accepting IMMEDIATELY — MIRROR of the wrong-tap branch below
          // (T-e193fa was fixed on the failure side only). Without this, the tiles stay live
          // for the 420ms until playSeq() re-arms, with inputIdx stale at the OLD length (a
          // valid index into the new longer seq), so a stray tap is scored against the wrong
          // sequence — ~75% ends the game a level early, ~25% double-fires nextRound(). playSeq
          // sets accepting=true again when the new playback ends. (Audit B, 2026-08-08.)
          accepting = false;
          flashVerdict(ctx.stage, true);
          best = len; len++;
          if (len > 8) return finish();
          nextRound();
        }
      } else {
        // stop accepting IMMEDIATELY on the wrong tap — leaving it true for the 420ms until
        // finish() let a "correcting" tap re-enter, advance the sequence, even start the next
        // round, while the stale finish timer still fired over it (T-e193fa)
        accepting = false;
        flashVerdict(ctx.stage, false); setTimeout(finish, 420);
      }
    }
    var finished = false;
    function finish() {
      if (finished) return;   // one done() per round, whatever path scheduled it
      finished = true;
      done({
        type: 'sequence', correct: best >= item.start, span: best,
        rtMs: meanOf(tapLats), adaptive: true,
        raw: { span: best, taps: tapLats.length, durationMs: Math.round(now() - t0) }
      });
    }
    nextRound();
  }

  /* ====================================================== ROUND 5 — quick maths */
  function playMaths(ctx, item, done) {
    var s = ctx.s, stage = ctx.stage;
    clear(stage);
    stage.appendChild(el('p', 'bzg-prompt', s.quickMaths));
    var sum = el('div', 'bzg-sum');
    stage.appendChild(sum);
    var opts = el('div', 'bzg-opts');
    stage.appendChild(opts);

    var trial = 0, hits = 0, rts = [], t0 = 0, answer = 0;

    function nextTrial() {
      if (trial >= item.trials) return finish();
      trial++;
      var op = item.ops[Math.floor(Math.random() * item.ops.length)];
      var a = 2 + Math.floor(Math.random() * item.max);
      var b = 2 + Math.floor(Math.random() * item.max);
      if (op === '-' && b > a) { var tmp = a; a = b; b = tmp; }
      answer = op === '+' ? a + b : op === '-' ? a - b : a * b;
      sum.textContent = a + ' ' + op + ' ' + b;

      var choices = [answer];
      while (choices.length < 4) {
        var d = answer + (Math.floor(Math.random() * 11) - 5);
        if (d !== answer && d >= 0 && choices.indexOf(d) === -1) choices.push(d);
      }
      clear(opts);
      shuffle(choices, Math.random).forEach(function (c) {
        var b2 = el('button', 'bzg-opt', String(c));
        b2.type = 'button';
        onTap(b2, function () {
          rts.push(now() - t0);
          if (c === answer) hits++;
          nextTrial();
        });
        opts.appendChild(b2);
      });
      t0 = now();
    }
    function finish() {
      var m = rts.length ? rts.reduce(function (x, y) { return x + y; }, 0) / rts.length : null;
      done({
        type: 'maths', correct: hits >= Math.ceil(item.trials * 0.6),
        accuracy: hits / item.trials, rtMs: m,
        raw: { hits: hits, trials: item.trials }
      });
    }
    nextTrial();
  }


  /* ============================ ROUND 7 — word recognition / false memory (NEW)
   * Calum 2026-08-05 asked for a word round. Built as the DRM paradigm (Deese 1959,
   * Roediger & McDermott 1995) rather than plain recall: the probe list contains a
   * semantically-related LURE that was never shown. Most people confidently "recognise"
   * it. That is the finding — and it is a far better payoff than "you remembered 3 of 5",
   * because being fooled is universal and interesting rather than a deficit. */
  function playWords(ctx, item, done) {
    var s = ctx.s, stage = ctx.stage;
    var lex = ctx.opts.wordLists;
    var list = (lex && lex[item.listIndex % lex.length]) || null;
    if (!list) { return done({ type: 'words', correct: true, accuracy: 1, rtMs: null, raw: { skipped: 'no-lexicon' } }); }

    clear(stage);
    stage.appendChild(el('p', 'bzg-prompt', s.rememberWords));
    var slot = el('div', 'bzg-word');
    stage.appendChild(slot);

    var i = 0;
    var STUDY_MS = REDUCED_MOTION ? 1100 : 800, GAP_MS = 180;
    (function showNext() {
      if (i >= list.study.length) { setTimeout(testPhase, 480); return; }
      slot.textContent = list.study[i++];
      setTimeout(function () { slot.textContent = ''; setTimeout(showNext, GAP_MS); }, STUDY_MS);
    })();

    function testPhase() {
      // 4 probes: 2 genuinely-studied, the critical lure, 1 unrelated new word.
      var probes = [
        { w: list.study[1], seen: true,  kind: 'old' },
        { w: list.lure,     seen: false, kind: 'lure' },
        { w: list.study[3], seen: true,  kind: 'old' },
        { w: list.new[0],   seen: false, kind: 'new' }
      ];
      probes = shuffle(probes, Math.random);
      var idx = 0, hits = 0, lureFooled = false, t0 = 0, rts = [];

      function ask() {
        if (idx >= probes.length) {
          var mean = rts.length ? rts.reduce(function (a, b) { return a + b; }, 0) / rts.length : null;
          return done({
            type: 'words', correct: hits >= 3, accuracy: hits / probes.length,
            rtMs: mean, lureFooled: lureFooled, lureWord: list.lure,
            raw: { hits: hits, probes: probes.length }
          });
        }
        var p = probes[idx++];
        clear(stage);
        stage.appendChild(el('p', 'bzg-prompt', s.wasItShown));
        var w = el('div', 'bzg-word bzg-word--probe', p.w);
        stage.appendChild(w);
        var opts = el('div', 'bzg-opts');
        // one answer per probe — a double-tap advanced idx twice (skipping a probe mid-list)
        // and double-fired done() on the last one (T-e193fa)
        var answered = false;
        [{ k: true, l: s.seenIt }, { k: false, l: s.newWord }].forEach(function (o) {
          var b = el('button', 'bzg-opt', o.l);
          b.type = 'button';
          b.style.touchAction = 'manipulation';
          onTap(b, function () {
            if (answered) return;
            answered = true;
            rts.push(now() - t0);
            var okP = (o.k === p.seen);
            if (okP) hits++;
            if (p.kind === 'lure' && o.k === true) lureFooled = true;
            flashVerdict(stage, okP);
            setTimeout(ask, 380);
          });
          opts.appendChild(b);
        });
        stage.appendChild(opts);
        t0 = now();
      }
      ask();
    }
  }


  /* ============================ ROUND — odd one out (visual search)
   * Replaces change blindness in the default set. Same "find the different one" instinct
   * Calum originally asked for, but legible in under a second with no instruction to read.
   *
   * Two trials by design: POP-OUT (target differs on ONE feature — colour) then CONJUNCTION
   * (target differs only by a COMBINATION of colour AND shape). The gap between them is the
   * finding: pop-out is flat regardless of how many distractors there are, conjunction forces
   * you to check items one at a time. (Treisman feature-integration.) */
  function playOddOne(ctx, item, done) {
    var s = ctx.s, stage = ctx.stage;
    var SHAPES = ['●', '■'];
    var C1 = '#0A5FB8', C2 = '#E0A800';
    var trial = 0, rts = [], hits = 0;

    function run() {
      trial++;
      var conj = (trial === 2);
      clear(stage);
      stage.appendChild(el('p', 'bzg-prompt', s.oddOneOut));
      var n = item.grid * item.grid;
      var grid = el('div', 'bzg-grid bzg-grid--odd');
      grid.style.gridTemplateColumns = 'repeat(' + item.grid + ', 1fr)';
      var target = Math.floor(Math.random() * n);

      for (var i = 0; i < n; i++) {
        var cell = el('button', 'bzg-odd');
        cell.type = 'button';
        cell.style.touchAction = 'manipulation';
        var shape, colour;
        if (!conj) {
          // pop-out: everything shape A colour 1; target is colour 2
          shape = SHAPES[0]; colour = (i === target) ? C2 : C1;
        } else {
          // conjunction: half A/C1 and half B/C2 distractors; target is A/C2
          if (i === target) { shape = SHAPES[0]; colour = C2; }
          else if (i % 2 === 0) { shape = SHAPES[0]; colour = C1; }
          else { shape = SHAPES[1]; colour = C2; }
        }
        cell.textContent = shape;
        cell.style.color = colour;
        (function (idx) {
          cell.addEventListener('pointerdown', function (ev) {
            if (ev.cancelable) ev.preventDefault();
            if (locked) return;
            locked = true;
            rts.push(now() - t0);
            var okO = (idx === target);
            if (okO) hits++;
            flashVerdict(stage, okO);
            setTimeout(function () {
              if (trial < 2) run();
              else {
                done({ type: 'oddone', correct: hits > 0, accuracy: hits / 2,
                       rtMs: rts.length ? rts.reduce(function (a, b) { return a + b; }, 0) / rts.length : null,
                       popMs: rts[0], conjMs: rts[1],
                       raw: { rts: rts.map(Math.round), hits: hits } });
              }
            }, 460);
          });
        })(i);
        grid.appendChild(cell);
      }
      stage.appendChild(grid);
      var locked = false, t0 = now();
    }
    run();
  }


  /* ============================ ROUND — subitising (how many dots?)
   * Up to about four you simply KNOW; past that you are counting, and it shows in the time.
   * Zero text, zero instruction, zero localisation. One of the fastest things to "get". */
  function playSubitise(ctx, item, done) {
    var s = ctx.s, stage = ctx.stage;
    var TRIALS = item.trials || 4;
    var trial = 0, hits = 0, smallRT = [], largeRT = [];

    function run() {
      trial++;
      clear(stage);
      stage.appendChild(el('p', 'bzg-prompt', s.howMany));
      var box = el('div', 'bzg-dotbox');
      var small = (trial % 2 === 1);
      var count = small ? (2 + Math.floor(Math.random() * 3)) : (6 + Math.floor(Math.random() * 4));
      for (var i = 0; i < count; i++) {
        var d = el('i', 'bzg-dot');
        d.style.left = (8 + Math.random() * 82) + '%';
        d.style.top  = (10 + Math.random() * 74) + '%';
        box.appendChild(d);
      }
      // a fixation beat first — testers read the unannounced flash as "too short" when the
      // exposure was already twice the research standard. The surprise was the problem, not the ms.
      var ready = el('div', 'bzg-ready');   // a CROSS, not a dot — see CSS
      stage.appendChild(ready);
      setTimeout(function () {
        if (ready.parentNode) ready.parentNode.removeChild(ready);
        stage.appendChild(box);
        setTimeout(afterFlash, item.flashMs || 700);
      }, 620);

      function afterFlash() {
        clear(stage);
        stage.appendChild(el('p', 'bzg-prompt', s.howMany));
        var opts = el('div', 'bzg-opts');
        var choices = [count];
        while (choices.length < 4) {
          var c = Math.max(1, count + Math.floor(Math.random() * 5) - 2);
          if (choices.indexOf(c) === -1) choices.push(c);
        }
        var t0 = now();
        // one answer per trial — without the latch, a second tap inside the 460ms transition
        // window scored again, and on the last trial double-fired done() (T-e193fa)
        var answered = false;
        shuffle(choices, Math.random).forEach(function (c) {
          var b = el('button', 'bzg-opt', String(c));
          b.type = 'button'; b.style.touchAction = 'manipulation';
          b.addEventListener('pointerdown', function (ev) {
            if (ev.cancelable) ev.preventDefault();
            if (answered) return;
            answered = true;
            (small ? smallRT : largeRT).push(now() - t0);
            // large sets are beyond the subitising range BY DESIGN — you are meant to estimate,
            // not count, so +/-1 counts as right. Small sets must be exact.
            var ok = small ? (c === count) : (Math.abs(c - count) <= 1);
            if (ok) hits++;
            flashVerdict(stage, ok);
            if (trial < TRIALS) setTimeout(run, 460);
            else setTimeout(function () {
              var mean = function (a) { return a.length ? a.reduce(function (x, y) { return x + y; }, 0) / a.length : null; };
              done({ type: 'subitise', correct: hits >= Math.ceil(TRIALS * 0.5),
                     accuracy: hits / TRIALS, rtMs: mean(smallRT.concat(largeRT)),
                     smallMs: mean(smallRT), largeMs: mean(largeRT),
                     raw: { hits: hits, trials: TRIALS } });
            }, 460);
          });
          opts.appendChild(b);
        });
        stage.appendChild(opts);
      }
    }
    run();
  }

  /* ============================ ROUND — the Simon effect
   * A coloured square appears on the LEFT or RIGHT. You answer by COLOUR, not position.
   * When colour and side disagree you slow down. Same conflict science as Stroop but with
   * ZERO READING — fairer across markets and free to localise. */
  function playSimon(ctx, item, done) {
    var s = ctx.s, stage = ctx.stage;
    var LEFT = { key: 'blue', hex: '#0A5FB8' }, RIGHT = { key: 'yellow', hex: '#E0A800' };
    var TRIALS = item.trials || 8;
    var trial = 0, hits = 0, congRT = [], incRT = [];

    clear(stage);
    stage.appendChild(el('p', 'bzg-prompt', s.matchColour));
    var field = el('div', 'bzg-field');
    var sq = el('div', 'bzg-simon-sq');
    field.appendChild(sq);
    stage.appendChild(field);
    var row = el('div', 'bzg-opts');
    [LEFT, RIGHT].forEach(function (o) {
      var b = el('button', 'bzg-opt bzg-opt--swatch');
      b.type = 'button'; b.style.touchAction = 'manipulation';
      b.style.background = o.hex; b.style.borderColor = o.hex;
      b.setAttribute('aria-label', o.key);
      b.addEventListener('pointerdown', function (ev) {
        if (ev.cancelable) ev.preventDefault();
        answer(o.key);
      });
      row.appendChild(b);
    });
    stage.appendChild(row);

    var cur = null, t0 = 0, congruent = false, waiting = false;
    function nextTrial() {
      if (trial >= TRIALS) return finish();
      trial++;
      var colour = Math.random() < 0.5 ? LEFT : RIGHT;
      var onLeft = Math.random() < 0.5;
      congruent = (colour === LEFT && onLeft) || (colour === RIGHT && !onLeft);
      cur = colour.key;
      sq.style.background = colour.hex;
      sq.style.left = onLeft ? '8%' : 'auto';
      sq.style.right = onLeft ? 'auto' : '8%';
      sq.style.visibility = 'visible';
      t0 = now(); waiting = true;
    }
    function answer(k) {
      if (!waiting) return;
      waiting = false;
      (congruent ? congRT : incRT).push(now() - t0);
      var okS = (k === cur);
      if (okS) hits++;
      flashVerdict(stage, okS);
      sq.style.visibility = 'hidden';
      setTimeout(nextTrial, 380);
    }
    function finish() {
      var mean = function (a) { return a.length ? a.reduce(function (x, y) { return x + y; }, 0) / a.length : null; };
      var mc = mean(congRT), mi = mean(incRT);
      done({ type: 'simon', correct: hits >= Math.ceil(TRIALS * 0.6), accuracy: hits / TRIALS,
             rtMs: mean(congRT.concat(incRT)),
             simonMs: (mc != null && mi != null) ? (mi - mc) : null,
             raw: { hits: hits, trials: TRIALS } });
    }
    setTimeout(nextTrial, 600);
  }

  /* ============================ ROUND — line bisection
   * Tap the middle of a line. Most people land slightly LEFT of true centre, consistently
   * (pseudoneglect). One tap, nothing to read, genuinely surprising result. */
  function playBisect(ctx, item, done) {
    var s = ctx.s, stage = ctx.stage;
    var TRIALS = item.trials || 3;
    var trial = 0, errs = [], signed = [];

    function run() {
      trial++;
      clear(stage);
      stage.appendChild(el('p', 'bzg-prompt', s.tapMiddle));
      var wrap = el('div', 'bzg-linewrap');
      var line = el('div', 'bzg-line');
      line.style.width = (70 + (trial % 3) * 10) + '%';
      wrap.appendChild(line);
      stage.appendChild(wrap);
      var t0 = now();
      line.style.touchAction = 'manipulation';
      line.addEventListener('pointerdown', function (ev) {
        if (ev.cancelable) ev.preventDefault();
        var r = line.getBoundingClientRect();
        var x = (ev.clientX - r.left) / r.width;
        var sErr = (x - 0.5) * 100;
        signed.push(sErr); errs.push(Math.abs(sErr));
        var mark = el('i', 'bzg-mark');
        mark.style.left = (x * 100) + '%';
        line.appendChild(mark);
        setTimeout(function () {
          if (trial < TRIALS) run();
          else {
            var mean = function (a) { return a.reduce(function (q, w) { return q + w; }, 0) / a.length; };
            done({ type: 'bisect', correct: true, accuracy: Math.max(0, 1 - mean(errs) / 20),
                   rtMs: now() - t0, offPct: mean(errs), biasPct: mean(signed),
                   raw: { signed: signed.map(function (v) { return Math.round(v * 10) / 10; }) } });
          }
        }, 520);
      });
    }
    run();
  }


  /* ============================ ROUND — task switching
   * Two rules alternate. The SAME two buttons mean different things depending on which rule
   * is in play, so every rule change forces a reconfiguration. The cost of that reconfiguration
   * is the finding — and it is the honest answer to "I'm good at multitasking", which almost
   * nobody is: you are not doing two things at once, you are paying a toll to swap between them. */
  function playSwitch(ctx, item, done) {
    var s = ctx.s, stage = ctx.stage;
    var C_BLUE = '#0A5FB8', C_YELL = '#E0A800';
    var TRIALS = item.trials || 10;
    var trial = 0, hits = 0, repeatRT = [], switchRT = [], lastRule = null;

    clear(stage);
    var cue = el('div', 'bzg-cue');
    stage.appendChild(cue);
    var card = el('div', 'bzg-switchcard');
    stage.appendChild(card);
    var opts = el('div', 'bzg-opts');
    stage.appendChild(opts);

    var t0 = 0, waiting = false, answerKey = null;

    function nextTrial() {
      if (trial >= TRIALS) return finish();
      trial++;
      var rule = Math.random() < 0.5 ? 'colour' : 'shape';
      var isSwitch = (lastRule !== null && rule !== lastRule);
      lastRule = rule;

      var shape = Math.random() < 0.5 ? 'circle' : 'square';
      var colour = Math.random() < 0.5 ? 'blue' : 'yellow';
      answerKey = (rule === 'colour') ? colour : shape;

      cue.textContent = (rule === 'colour') ? s.byColour : s.byShape;
      cue.style.background = (rule === 'colour') ? 'rgba(10,95,184,.12)' : 'rgba(109,110,113,.14)';
      card.textContent = (shape === 'circle') ? '\u25cf' : '\u25a0';
      card.style.color = (colour === 'blue') ? C_BLUE : C_YELL;

      clear(opts);
      var pair = (rule === 'colour')
        ? [{ k: 'blue', swatch: C_BLUE }, { k: 'yellow', swatch: C_YELL }]
        : [{ k: 'circle', glyph: '\u25cf' }, { k: 'square', glyph: '\u25a0' }];
      pair.forEach(function (o) {
        var b = el('button', 'bzg-opt');
        b.type = 'button'; b.style.touchAction = 'manipulation';
        if (o.swatch) { b.style.background = o.swatch; b.style.borderColor = o.swatch; b.style.minHeight = '54px'; }
        else { b.textContent = o.glyph; b.style.fontSize = '26px'; }
        b.setAttribute('aria-label', o.k);
        b.addEventListener('pointerdown', function (ev) {
          if (ev.cancelable) ev.preventDefault();
          if (!waiting) return;
          waiting = false;
          var rt = now() - t0;
          (isSwitch ? switchRT : repeatRT).push(rt);
          var okW = (o.k === answerKey);
          if (okW) hits++;
          flashVerdict(stage, okW);
          setTimeout(nextTrial, 380);
        });
        opts.appendChild(b);
      });
      t0 = now(); waiting = true;
    }
    function finish() {
      var mean = function (a) { return a.length ? a.reduce(function (x, y) { return x + y; }, 0) / a.length : null; };
      var mr = mean(repeatRT), ms = mean(switchRT);
      done({ type: 'switch', correct: hits >= Math.ceil(TRIALS * 0.6), accuracy: hits / TRIALS,
             rtMs: mean(repeatRT.concat(switchRT)),
             switchCostMs: (mr != null && ms != null) ? (ms - mr) : null,
             raw: { hits: hits, trials: TRIALS } });
    }
    setTimeout(nextTrial, 500);
  }

  /* ============================ ROUND — attentional blink
   * A rapid stream with two coloured targets. If the second lands roughly 200-500ms after the
   * first, most people simply do not see it — attention has a refractory period. Demonstrating
   * that on someone's own eyes is far more convincing than describing it. */
  function playBlink(ctx, item, done) {
    var s = ctx.s, stage = ctx.stage;
    var TRIALS = item.trials || 4;
    var trial = 0, hits = 0;
    var shortHits = 0, shortN = 0, longHits = 0, longN = 0;   /* blink stats: PAIR trials only */
    var GLYPHS = ['\u25b2', '\u25a0', '\u25cf', '\u2b1f', '\u2726', '\u25c6'];

    /* THE COUNT VARIES (Calum 2026-08-18, T-c236d5). It used to be a fixed two every single time,
       with the instructions saying so and the grader hardcoded to `c === 2` — so anyone who read
       the instructions scored 100% without looking at the screen and the round measured nothing.
       `n` is now drawn per trial and the `targets` array below is the SINGLE source of truth for
       both what gets painted and what gets accepted, so the two cannot drift apart. That drift is
       the real hazard: an earlier attempt at this fix derived the answer from a counter that was
       itself always 2 — it read as correct in review and changed nothing at runtime.
       Drawn from a balanced shuffled bag, not uniformly at random, so that every answerable count
       actually occurs in a short round while 2 stays the modal case — the T1->T2 lag manipulation
       is the actual blink probe and still needs trials to measure. */
    function countBag(n) {
      var bag = [], base = [2, 1, 3, 2];
      while (bag.length < n) bag = bag.concat(base);
      bag = bag.slice(0, n);
      for (var i = bag.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1)), t = bag[i];
        bag[i] = bag[j]; bag[j] = t;
      }
      return bag;
    }
    var COUNTS = countBag(TRIALS);
    /* Lag was previously assigned by trial parity. With the count varying, that decouples the lag
       from the trials that can actually carry the blink probe, so a round could spend both of its
       clean n=2 trials on the same lag and measure nothing. Assign short/long ACROSS the n=2
       trials instead, so the short-vs-long comparison always gets both cells when both exist. */
    var LAGSHORT = (function () {
      var flags = [], k = 0;
      for (var i = 0; i < COUNTS.length; i++) {
        if (COUNTS[i] === 2) { flags.push(k % 2 === 0); k++; }
        else { flags.push(i % 2 === 0); }        /* non-pair trials: lag is cosmetic, unmeasured */
      }
      return flags;
    })();

    function run() {
      trial++;
      var shortLag = LAGSHORT[trial - 1];          // balanced across the clean n=2 trials
      clear(stage);
      stage.appendChild(el('p', 'bzg-prompt', s.countBlue));
      var slot = el('div', 'bzg-stream');
      stage.appendChild(slot);

      var ITEM_MS = item.itemMs || 110;
      var t1 = 4 + Math.floor(Math.random() * 2);              // first target early
      var lag = shortLag ? 2 : 6;                              // ~220ms vs ~660ms
      var n = COUNTS[trial - 1] || 2;                          // how many BLUE this trial
      var targets = [t1];
      if (n >= 2) targets.push(t1 + lag);
      if (n >= 3) targets.push(t1 + lag + 4);                  // third, clearly separated
      /* Always leave a couple of distractors after the last target — a target on the final frame
         is a different, and unfairly harder, task than one inside the stream. */
      var len = Math.max(16, targets[targets.length - 1] + 3);
      var i = 0;
      (function tick() {
        if (i >= len) { setTimeout(ask, 340); return; }
        var isTarget = targets.indexOf(i) !== -1;
        slot.textContent = GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
        slot.style.color = isTarget ? '#0A5FB8' : '#b9c2cc';
        i++;
        setTimeout(function () { slot.textContent = ''; setTimeout(tick, 24); }, ITEM_MS);
      })();

      function ask() {
        clear(stage);
        stage.appendChild(el('p', 'bzg-prompt', s.howManyBlue));
        var opts = el('div', 'bzg-opts');
        // one answer per trial — a double-tap in the 460ms window counted twice and could
        // double-fire done() on the final trial (T-e193fa)
        var answered = false;
        [1, 2, 3].forEach(function (c) {
          var b = el('button', 'bzg-opt', String(c));
          b.type = 'button'; b.style.touchAction = 'manipulation';
          b.addEventListener('pointerdown', function (ev) {
            if (ev.cancelable) ev.preventDefault();
            if (answered) return;
            answered = true;
            var right = (c === targets.length);   /* exact match against what was PAINTED */
            if (right) hits++;
            /* Blink stats come ONLY from clean 2-target trials. n=1 has no second target to
               miss; n=3 adds a third far-lagged target, so a miscount there may be the THIRD one
               rather than T2 — folding either in would attribute a non-blink miss to the blink. */
            if (targets.length === 2) {
              if (shortLag) { shortN++; if (right) shortHits++; }
              else { longN++; if (right) longHits++; }
            }
            flashVerdict(stage, right);
            if (trial < TRIALS) setTimeout(run, 460);
            else setTimeout(function () {
              done({ type: 'blink',
                     correct: hits > 0,
                     accuracy: hits / Math.max(1, TRIALS),
                     rtMs: null,
                     shortAcc: shortN ? shortHits / shortN : null,
                     longAcc: longN ? longHits / longN : null,
                     raw: { hits: hits, trials: TRIALS, counts: COUNTS,
                            shortHits: shortHits, shortN: shortN,
                            longHits: longHits, longN: longN } });
            }, 460)
          });
          opts.appendChild(b);
        });
        stage.appendChild(opts);
      }
    }
    run();
  }

  /* ------------------------------------------------------------- scoring */
  /**
   * Composite score. Deliberately bounded so no result lands in a "you did badly" band —
   * the floor is 40, not 0. Percentile is supplied by the caller from a PER-MARKET
   * player pool. NEVER age-normed (see tactics.md §3).
   */
  function scoreRound(r) {
    var acc = (r.accuracy != null) ? r.accuracy : (r.correct ? 1 : 0);
    var speed = 0.5;
    if (r.rtMs != null && r.rtMs > 0) {
      // 250ms → ~1.0, 2000ms → ~0.1; monotonic, no hard cliffs
      // recentred: 300ms -> ~0.95, 800ms -> ~0.6, 2500ms -> ~0.15. The old curve treated
      // 250ms as the reference, which almost no real response beats, so it saturated low.
      speed = Math.max(0.05, Math.min(1, 1 - (Math.log(Math.max(r.rtMs, 250) / 300) / Math.log(28))));
    }
    if (r.type === 'sequence' && r.span != null) acc = Math.min(1, r.span / 6);
    // Speed only counts for answers you got RIGHT. The old form was acc*0.6 + speed*0.4, which
    // paid the speed term REGARDLESS of accuracy — so answering fast and wrong still banked 40%,
    // and any round with no timing collected 0.5 of it for free. That is how a deliberately-wrong
    // run still scored 72 (Calum, live 2026-08-07). Speed now AMPLIFIES accuracy rather than
    // substituting for it: all wrong -> 0, right and slow -> 60, right and fast -> 100.
    return Math.round(acc * (0.6 + 0.4 * speed) * 100);
  }
  /**
   * Composite. Calum 2026-08-06: "I seem to always be getting around 70 or 80 and this seems to
   * happen every time." He is right and it was a real flaw — the old curve was
   * 40 + raw*0.6, which squeezed the whole 0-100 range of performance into 40-100 and put
   * typical play (raw 50-67) into a 70-80 band. A score that never moves cannot support
   * "beat your best", which is the entire replay mechanic.
   *
   * Now: floor 25, and the mid-range is stretched so ordinary variation is visible. The floor
   * still exists — nobody should be shown a demoralising number on a brand whose visitors
   * arrive because they struggle with focus — it is just no longer swallowing the signal.
   */
  function compositeScore(results) {
    if (!results.length) return 25;
    var sum = results.reduce(function (a, r) { return a + scoreRound(r); }, 0);
    var raw = sum / results.length;                 // 0-100
    var stretched = Math.pow(raw / 100, 0.85) * 100; // lift the middle, keep the ends honest
    // Floor lowered 25 -> 10. A floor exists so nobody is shown a demoralising number, but at 25
    // (with the old speed leak) a wrong run could not fall below the 60s. A wrong run must be able
    // to look wrong.
    return Math.round(10 + stretched * 0.90);       // 10 floor, 100 ceiling
  }

  /**
   * ADAPTIVE rounds keep going until the player fails, so their duration RISES with skill.
   * Any such round must be excluded from any "faster is better" aggregate. Declared in two
   * places on purpose: the round sets `adaptive: true` on its result, and the type is listed
   * here — so a new adaptive round that forgets one still gets caught by the other. Getting
   * this wrong is exactly the defect Calum found (see playSequence).
   */
  var ADAPTIVE_ROUNDS = { sequence: true };
  function isAdaptive(res) {
    return !!(res && (res.adaptive === true || ADAPTIVE_ROUNDS[res.type] === true));
  }

  /**
   * Bind a tap handler that fires on PRESS, not release, while keeping the keyboard working.
   *
   * `click` fires on RELEASE, and on touch it does not fire at all if the finger drifts a few
   * pixels. That is three separate defects in this project: measurable input lag in the reaction
   * round, again in the sequence round, and finally the science-card Next button, where taps
   * appeared to do nothing and then jumped the player two or three rounds forward because each one
   * had in fact advanced (Calum, live, 2026-08-07). Fixing the reported instance and not sweeping
   * the rest is what let it recur twice.
   *
   * It also corrupted the measurements: four ANSWER buttons inside timed rounds were on `click`,
   * so every latency recorded through them carried the release delay.
   *
   * Deliberately no preventDefault: the synthetic click must still arrive to clear the flag, so a
   * later keyboard activation is not swallowed. `touch-action: manipulation` (already set in CSS)
   * is what removes the 300ms tap delay.
   */
  function onTap(node, fn) {
    var viaPointer = false;
    node.addEventListener('pointerdown', function (ev) {
      viaPointer = true;
      fn.call(node, ev);
    });
    node.addEventListener('click', function (ev) {
      var handled = viaPointer;
      viaPointer = false;
      if (handled) return;          // pointerdown already ran it
      fn.call(node, ev);            // keyboard (Enter/Space) reaches us only here
    });
    return node;
  }

  /* ------------------------------------------- compliance runtime assertion */
  /**
   * Score→product connective copy, which is the thing that turns a cognitive test into a medical
   * recommendation. Routing must come from the preference question, never the score.
   *
   * ⚠ EXTENDED TO DE/FR 2026-08-06. This list was nine lowercase ENGLISH literals guarding a
   * FOUR-market deployment — it protected UK/US and did nothing at all for German and French, where
   * the identical breach would have sailed through. Adding a market means adding its phrases here.
   */
  var BANNED_CONNECTIVE = [
    // EN
    'based on your result', 'based on your score', 'matched to your score',
    'matched to your result', 'since you scored', 'because you scored',
    'because your focus', 'your score means you need', 'improve your score with',
    // DE
    'basierend auf deinem ergebnis', 'basierend auf ihrem ergebnis',
    'aufgrund deines ergebnisses', 'aufgrund ihres ergebnisses',
    'passend zu deinem ergebnis', 'weil du erzielt', 'dein ergebnis zeigt, dass du',
    'dein ergebnis bedeutet', 'verbessere deinen wert mit',
    // FR
    'd’après votre résultat', "d'après votre résultat", 'selon votre résultat',
    'en fonction de votre résultat', 'adapté à votre score', 'adapté à votre résultat',
    'parce que vous avez obtenu', 'votre résultat montre que vous',
    'votre score signifie', 'améliorez votre score avec'
  ];
  function assertNoConnective(text) {
    var low = String(text || '').toLowerCase();
    for (var i = 0; i < BANNED_CONNECTIVE.length; i++) {
      if (low.indexOf(BANNED_CONNECTIVE[i]) !== -1) {
        throw new Error('bz-game: banned score->product connective copy: "' + BANNED_CONNECTIVE[i] + '"');
      }
    }
    return true;
  }

  /* ------------------------------------------------------------ orchestrator */
  var RENDERERS = {
    reaction: playReaction, change: playChange, stroop: playStroop,
    sequence: playSequence, maths: playMaths, timing: playTiming, words: playWords,
    oddone: playOddOne, subitise: playSubitise, simon: playSimon, bisect: playBisect,
    switch: playSwitch, blink: playBlink
  };
  /**
   * FIXED ROUND ORDER (Calum 2026-08-06, REVERSING my mechanic-level randomisation).
   *
   * He is right, and the decisive reason is stronger than convention: a varying line-up
   * BREAKS THE SCORE. He chose composite score + percentile — if one play is subitising +
   * reaction and the next is words + sequence, the two totals are not comparable, so
   * "beat your best" is meaningless and the percentile has no stable measure to norm.
   * Randomising mechanics contradicted a decision already taken.
   *
   * His original chance instinct — "you don't know what questions are going to be shown" —
   * is already delivered by the randomised ITEM pools within each round. That stays.
   *
   * The ORDER below is not arbitrary. It runs easiest-to-grasp first and most effortful last:
   *   1-4  INSTANT     understood at a glance, nothing to read
   *   5-8  RULE        needs one beat to grasp, by which point they are invested
   *   9    TWO-PHASE   study then test; best payoff, so it earns a late slot
   *   10   EFFORTFUL   maths last — Calum: "the harder ones are the maths ones for a lot of
   *                    people". Sums at the start is where people quit.
   *
   * Shipping a shorter game = take a SUBSET, but always keep this relative order, so scores
   * stay comparable between players and between plays.
   */
  var FIXED_ORDER = [
    'reaction',   // 1  instant  · warm-up, rated good in playtest
    'subitise',   // 2  instant  · dots, no instruction needed
    'oddone',     // 3  instant  · visual search, replaced change blindness
    'sequence',   // 4  rule     · watch-and-repeat, rated "very good"
    'simon',      // 5  rule     · conflict, zero reading
    'stroop',     // 6  rule     · conflict, needs reading (Simon's comparison partner)
    'switch',     // 7  rule     · task-switching cost — the honest answer to "I multitask well"
    'timing',     // 8  rule     · anticipation, Calum's idea
    'blink',      // 9  2-phase  · attentional blink; needs a settled viewer, so late
    'words'       // 10 2-phase  · false-memory reveal, strongest payoff
  ];
  /* WITHDRAWN 2026-08-06 (both retained in RENDERERS/POOLS, so recoverable):
   *  - 'bisect'  Calum's testers: "didn't work properly… didn't seem obvious". CAUSE: the line
   *              is an 8px-tall hit target and the listener sits on the line itself, so a tap a
   *              few pixels high or low does NOTHING. That reads as broken, and it is. Fixable
   *              (wrap the line in a tall transparent strip and map the x-coordinate) — pulled
   *              rather than patched because Calum asked for it withdrawn.
   *  - 'maths'   Calum: "a lot of people get uncomfortable with maths and it just gives people
   *              who are good at maths an unfair advantage." Correct on the substance: arithmetic
   *              fluency is domain knowledge, not attention, so it confounds the thing we measure.
   */

  /** Take the first n of FIXED_ORDER, or order an explicit subset by it. */
  function orderedSlots(subset, n) {
    if (subset && subset.length) {
      var inOrder = FIXED_ORDER.filter(function (k) { return subset.indexOf(k) !== -1; });
      // anything requested that is NOT in the canonical order (a withdrawn mechanic being
      // deliberately re-enabled, e.g. bisect) is appended rather than silently dropped
      var extra = subset.filter(function (k) { return FIXED_ORDER.indexOf(k) === -1; });
      return inOrder.concat(extra);
    }
    return FIXED_ORDER.slice(0, n || FIXED_ORDER.length);
  }

  // 'change' (change blindness) is retained in RENDERERS/POOLS but REMOVED from the default
// set 2026-08-06: with the blank frame every cell flashes, so 'tap the square that keeps
// changing' described all of them. Authentic science, illegible task. Replaced by 'oddone'.
/**
 * ROUND COUNT LOCKED AT 10 (Calum keeps 10 while testing internally; 6-peer QC 2026-08-06 raised
 * the ambiguity to blocking). This number threads the Worker contract, the percentile key and the
 * E2E seed — `paced_ms` is a SUM, so runs with different round counts are not comparable and
 * `game_percentile` is keyed on `rounds`. It was `slice(0, 6)` while the Worker spec assumed 10,
 * which would have bucketed every real run against a population that never existed.
 * Change this in ONE place only, and the schema key follows it.
 */
var ROUND_COUNT = 10;
var DEFAULT_SLOTS = FIXED_ORDER.slice(0, ROUND_COUNT);

  function BZGame(opts) {
    this.opts = opts || {};
    this.market = this.opts.market || 'uk';
    // Market strings come from bz-game-i18n.js when supplied; STRINGS is the fallback only.
    this.s = this.opts.strings || STRINGS[this.opts.lang || 'en'];
    this.rnd = this.opts.daily ? makeRng(dailySeed(this.opts.market || 'uk')) : Math.random;
    // slots: an explicit subset is re-ordered to FIXED_ORDER so scores stay comparable
    // ROUND_COUNT, not a hardcoded literal. The old `|| 6` meant the live game ran SIX rounds while
    // ROUND_COUNT and DEFAULT_SLOTS both said ten — the constant was never on the runtime path, so
    // "locked at 10" was cosmetic and nothing caught it (Calum, live, 2026-08-07). One number, one
    // place, read by the code that actually runs.
    this.slots = orderedSlots(this.opts.slots, this.opts.rounds || ROUND_COUNT);
    this.results = [];
  }

  BZGame.prototype.mount = function (rootEl, onFinish) {
    this.root = rootEl;
    this.onFinish = onFinish;
    clear(rootEl);
    this.hud = el('div', 'bzg-hud');
    this.stage = el('div', 'bzg-stage');
    rootEl.appendChild(this.hud);
    rootEl.appendChild(this.stage);
    this.idx = 0;
    this.next();
  };


  /**
   * INSTRUCTION SCREEN (Calum 2026-08-06, from tester feedback: "players wanted to have the
   * rules or the instructions put in front of them first, where they could read them. When they
   * were ready they could just press the word GO, a big obvious button that's going to be
   * glowing").
   *
   * This also fixes a measurement problem: the round clock previously started when the round
   * RENDERED, so it included however long someone spent working out what to do. Now it starts
   * on GO, which makes the time comparable between a first-timer and a returning player — which
   * is what makes "I'll be faster next round" honest rather than an illusion.
   */
  BZGame.prototype.showInstructions = function (slot, cb) {
    var self = this;
    clear(this.stage);
    var card = el('div', 'bzg-instr');
    var howto = (this.opts.howto && this.opts.howto[slot]) || '';
    card.appendChild(el('h3', 'bzg-instr__name', (this.s.roundNames && this.s.roundNames[slot]) || ''));
    card.appendChild(el('p', 'bzg-instr__body', howto));
    var go = el('button', 'bzg-go', this.s.go);
    go.type = 'button';
    go.style.touchAction = 'manipulation';
    go.addEventListener('pointerdown', function (ev) {
      if (ev.cancelable) ev.preventDefault();
      cb();
    });
    card.appendChild(go);
    this.stage.appendChild(card);
    go.focus();
  };

  BZGame.prototype.next = function () {
    var self = this;
    if (this.idx >= this.slots.length) return this.finish();
    var slot = this.slots[this.idx];
    // Pools come from bz-game-pools.js when supplied; POOLS is the built-in fallback.
    var pools = this.opts.pools || POOLS;
    var item = drawFromPool(slot, pools[slot], this.rnd);

    // Calum 2026-08-05: the round counter existed but he did not notice it, and he asked for
    // a running timer for pressure. Both made prominent here.
    clear(this.hud);
    var head = el('div', 'bzg-hud__head');
    head.appendChild(el('span', 'bzg-round',
      this.s.round + ' ' + (this.idx + 1) + ' / ' + this.slots.length));
    var clock = el('span', 'bzg-clock', '0.0s');
    head.appendChild(clock);
    this.hud.appendChild(head);
    var barwrap = el('div', 'bzg-progress');
    var fill = el('i');
    // show the round you are ON, not rounds completed — a 0% bar on round 1 reads as 'nothing happened'
    fill.style.width = (((this.idx + 1) / this.slots.length) * 100) + '%';
    barwrap.appendChild(fill);
    this.hud.appendChild(barwrap);
    if (this._clockTimer) { clearInterval(this._clockTimer); this._clockTimer = null; }
    clock.textContent = '0.0s';   // starts on GO

    this._roundToken = (this._roundToken || 0) + 1;
    var myToken = this._roundToken;

    var ctx = { s: this.s, stage: this.stage, opts: this.opts };
    // instructions first; the clock does not start until they press GO
    this.showInstructions(slot, function () {
      self.startClock();
      RENDERERS[slot](ctx, item, onRoundDone);
    });

    function onRoundDone(res) {
      // A round renderer can still have a pending setTimeout when the player has already moved on
      // (a wrong tap schedules finish(), the player presses Next first). The late done() then made
      // showScience CLEAR THE STAGE and paint the previous round card over the new round, while the
      // HUD already read the new number — which is exactly 'round 5 showed nothing' (Calum, live
      // 2026-08-07): the counter advanced, the body did not, and the next press skipped the round.
      // One token per round kills the whole class rather than the two rounds that happened to show it.
      if (myToken !== self._roundToken) { return; }
      if (self._clockTimer) { clearInterval(self._clockTimer); self._clockTimer = null; }
      if (self._roundStart != null) {
        res.roundMs = now() - self._roundStart;
        self._totalMs = (self._totalMs || 0) + res.roundMs;
        // pacedMs excludes ADAPTIVE rounds, whose duration RISES with skill. totalMs is true
        // elapsed and stays honest for analytics; pacedMs is the only figure safe to hang
        // "beat that next time" on, because it is the only one improvement moves downward.
        if (!isAdaptive(res)) self._pacedMs = (self._pacedMs || 0) + res.roundMs;
      }
      res.slot = slot;
      self.results.push(res);
      self.showScience(res, function () { self.idx++; self.next(); });
    }
  };

  /** Clock starts on GO, not on render (see showInstructions). */
  BZGame.prototype.startClock = function () {
    var clock = this.hud.querySelector('.bzg-clock');
    if (!clock) return;
    if (this._clockTimer) clearInterval(this._clockTimer);
    this._roundStart = now();
    var self = this;
    this._clockTimer = setInterval(function () {
      clock.textContent = ((now() - self._roundStart) / 1000).toFixed(1) + 's';
    }, 100);
  };

  BZGame.prototype.showScience = function (res, cb) {
    var sci = SCIENCE[res.type];
    clear(this.stage);
    var card = el('div', 'bzg-science');
    card.appendChild(el('h3', 'bzg-science__name', sci.name));
    // Prefer the localised explainer; prepend the measured stat so the science stays
    // about the PHENOMENON while the number stays real and market-agnostic.
    var body;
    if (this.opts.science && this.opts.science[res.type]) {
      var stat = measuredStat(res, this.opts.stat);
      body = (stat ? stat + ' ' : '') + this.opts.science[res.type];
    } else {
      body = sci.explain(res);
    }
    assertNoConnective(body);                 // runtime guard, not just intent
    card.appendChild(el('p', 'bzg-science__body', body));
    var btn = el('button', 'bzg-btn', this.s.next);
    btn.type = 'button';
    // one advance per card — onTap dedupes press-vs-click, NOT press-press. A double-tap here
    // ran cb() twice and skipped the next round entirely (the "jumped 2-3 rounds" defect's
    // surviving form; caught by the T-e193fa latch test, not by any earlier sweep).
    var advanced = false;
    onTap(btn, function () {
      if (advanced) return;
      advanced = true;
      cb();
    });
    card.appendChild(btn);
    this.stage.appendChild(card);
    btn.focus();
  };

  BZGame.prototype.finish = function () {
    var score = compositeScore(this.results);
    // Session-only. Never persisted as a customer trait, never sent to CRM (tactics.md §4).
    this.onFinish({
      score: score,
      results: this.results,
      market: this.market,
      totalMs: this._totalMs || null,   // true elapsed — analytics only, NOT an improvement hook
      pacedMs: this._pacedMs || null,   // fixed-trial rounds only — the comparable figure
      // percentile is filled by the caller from the per-market player pool
      percentile: null
    });
  };

  root.BZGame = BZGame;
  root.BZGame.compositeScore = compositeScore;
  root.BZGame.scoreRound = scoreRound;
  root.BZGame.assertNoConnective = assertNoConnective;
  root.BZGame.onTap = onTap;   // shared so the segue binds taps identically
  root.BZGame.POOLS = POOLS;
  root.BZGame.DEFAULT_SLOTS = DEFAULT_SLOTS;
  root.BZGame.FIXED_ORDER = FIXED_ORDER;
  root.BZGame.orderedSlots = orderedSlots;
  root.BZGame.isAdaptive = isAdaptive;
  root.BZGame.ADAPTIVE_ROUNDS = ADAPTIVE_ROUNDS;
  root.BZGame.ROUND_COUNT = ROUND_COUNT;   // the Worker and the percentile key must agree with this
  root.BZGame._internals = { drawFromPool: drawFromPool, makeRng: makeRng, dailySeed: dailySeed };
})(window);
